import {
  ReplicantAsyncActionAPI,
  ReplicantEventHandlerAPI,
} from '@play-co/replicant';
import { MutableState, State } from '../../schema';
import { TradingSearchResult } from './offchainTrading.properties';
import { curveConfig, txConfig } from './offchainTrading.ruleset';
import { PriceSlice, TradingState, TradingTx } from './offchainTrading.schema';
import { TradingOverview, TradingTokenListing, TxType } from './types';
import Big from 'big.js';
import { ReplicantServer } from '../../config';

export function getCurvePrice(supply: Big) {
  return curveConfig.startPrice.mul(
    Math.exp(supply.mul(curveConfig.exponent).toNumber()),
  );
}

function getTokenAmountForCoinBuy(
  currentSupply: Big,
  coinsToSpend: Big,
  fee = txConfig.fee,
) {
  let coinsToSpendValue = coinsToSpend;
  if (!(coinsToSpend instanceof Big)) {
    coinsToSpendValue = Big(coinsToSpend);
  }
  const adjustedCoinsToSpend = coinsToSpendValue
    .mul(curveConfig.exponent)
    .div(curveConfig.startPrice.mul(txConfig.buyModifier).mul(1 + fee));
  const term = adjustedCoinsToSpend.add(
    Math.exp(currentSupply.mul(curveConfig.exponent).toNumber()),
  );
  const tokenAmount = Big(1)
    .div(curveConfig.exponent)
    .mul(
      Big(Math.log(term.toNumber())).minus(
        currentSupply.mul(curveConfig.exponent),
      ),
    );
  return tokenAmount;
}

export function getGrossCoinAmountForTokenSell(
  currentSupply: Big,
  tokenAmount: Big,
) {
  let tokenAmountDiff = currentSupply.minus(tokenAmount);
  if (tokenAmountDiff.lt(0)) {
    tokenAmountDiff = currentSupply;
  }

  const pricing = curveConfig.startPrice.div(curveConfig.exponent);

  const curve = Big(
    Math.exp(curveConfig.exponent.mul(currentSupply).toNumber()),
  );

  const cadence = Big(
    Math.exp(
      curveConfig.exponent.mul(currentSupply.minus(tokenAmount)).toNumber(),
    ),
  );

  return pricing.mul(curve.minus(cadence));
}

// (1 - spread) * (1 - fee) * (constant / exponent) * (EXP(exponent * (total supply + amount to sell)) - EXP(exponent * total supply))
export function getCoinAmountForTokenSell(
  currentSupply: Big,
  tokensToSell: Big,
) {
  const grossCurrencyAmount = getGrossCoinAmountForTokenSell(
    currentSupply,
    tokensToSell,
  );
  const netCurrencyAmount = grossCurrencyAmount.mul(
    txConfig.sellModifier.minus(txConfig.fee),
  );

  return netCurrencyAmount;
}

export function getMarketCap(supply: Big) {
  return getCurvePrice(supply).mul(supply);
}

export function getMyOffchainTokenIds(state: State) {
  return Object.keys(state.trading.offchainTokens);
}

export function getMyOffchainTokenTokenAmount(
  state: State,
  offchainTokenId: string,
) {
  return state.trading.offchainTokens[offchainTokenId]?.tokenAmount || '0';
}

function getOverview({
  details: { description },
  supply,
  holderCount,
}: TradingState): TradingOverview {
  let numericValueSupply = Big(supply);
  // @TODO: Put this in a const
  const shortDescription =
    description.length > 180
      ? `${description.substring(0, 180)}...`
      : description;
  return {
    shortDescription,
    marketCap: getMarketCap(numericValueSupply),
    numOfHolders: holderCount,
  };
}

function getDescription({
  details: { description, telegramChannelLink, telegramChatLink, twitterLink },
}: TradingState) {
  return {
    description,
    telegramChannelLink,
    telegramChatLink,
    twitterLink,
  };
}

export function getOffchainTokenListing(
  searchableOffchainToken: TradingSearchResult,
): TradingTokenListing {
  return {
    offchainTokenId: searchableOffchainToken.id,
    image: searchableOffchainToken.profile.image,
    name: searchableOffchainToken.profile.name,
    creatorName: searchableOffchainToken.profile.creatorName,
    creatorImage: searchableOffchainToken.profile.creatorImage,
    creatorId: searchableOffchainToken.profile.creatorId,
    marketCap: getMarketCap(Big(searchableOffchainToken.supply)),
    ticker: searchableOffchainToken.profile.ticker,
  };
}

export function getOffchainToken(
  state: TradingState & { createdAt: number },
  offchainTokenId: string,
) {
  const { image, name, creatorId, creatorName, ticker, creatorImage } =
    state.details;

  const curvePrice = getCurvePrice(Big(state.supply));

  return {
    id: offchainTokenId,
    image,
    name,
    creatorId,
    creatorName,
    creatorImage,
    ticker,
    // overview
    overview: getOverview(state),
    // description
    description: getDescription(state),
    // holders
    holderCount: state.holderCount,
    // transactions
    transactions: state.txs,
    // prices
    buyPrice: curvePrice,
    sellPrice: undefined,
    // supply
    supply: state.supply,
    // trends
    trends: state.trends,
    // metrics
    changePerHour: -1, // is this applied for both sell and buy or would be one for each?,
    status: state.status,
    createdAt: state.createdAt,
  };
}

export type TradingToken = ReturnType<typeof getOffchainToken>;

export interface CanBuyOpts {
  currencyAmount: Big;
  tokenAmountEstimate: Big;
  driftPct: number;
}

export function getCanBuy(state: TradingState, opts: CanBuyOpts) {
  const { currencyAmount, tokenAmountEstimate, driftPct } = opts;
  const buyTokenAmount = getBuyEstimate(state, currencyAmount);

  const minBuyTokenAmount = tokenAmountEstimate.minus(
    tokenAmountEstimate.mul(driftPct - 1),
  );

  return buyTokenAmount.gte(minBuyTokenAmount);
}

export interface CanSellOpts {
  tokenAmount: Big;
  currencyAmountEstimate: Big;
  driftPct: number;
}

export function getCanSell(state: TradingState, opts: CanSellOpts) {
  const { tokenAmount, currencyAmountEstimate, driftPct } = opts;
  const sellPrice = getSellEstimate(state, tokenAmount);

  const minSellPrice = currencyAmountEstimate.minus(
    currencyAmountEstimate.mul(driftPct - 1),
  );

  return sellPrice.gte(minSellPrice);
}

/**
 * Use this function to get the estimate token amount the user can purchase given the
 * currencyAmount. The estimate deducts the transactions fee. Use optional opts to modify the behaviour
 * @param state
 * @param currencyAmount
 * @returns the token amount that can be bought with the currencyAmount
 */
export const getBuyEstimate = (state: TradingState, currencyAmount: Big) => {
  return getBuyTxEstimate({
    currencyAmount,
    currentSupply: Big(state.supply),
  });
};

/**
 * Use this function to get the estimate amount of currencyAmount the user can get given the
 * tokenAmount it wants to sell
 * @param state
 * @param tokenAmount
 * @returns
 */
export const getSellEstimate = (state: TradingState, tokenAmount: Big) => {
  return getCoinAmountForTokenSell(Big(state.supply), tokenAmount);
};

export const getCreateEstimate = (currencyAmount: Big) => {
  return getTokenAmountForCoinBuy(Big(0), currencyAmount);
};

interface SellTx {
  tokenAmount: Big;
  currentSupply: Big;
}

interface BuyTx {
  currencyAmount: Big;
  currentSupply: Big;
}
export const getBuyTxEstimate = (opts: BuyTx) => {
  return getTokenAmountForCoinBuy(
    opts.currentSupply,
    opts.currencyAmount,
    txConfig.fee,
  );
};

export const getIsBuyTxValid = (userState: State, tx: BuyTx) => {
  return tx.currencyAmount.lte(userState.balance) && tx.currencyAmount.gt(0);
};

export const getIsSellTxValid = (
  userState: State,
  offchainTokenId: string,
  tx: SellTx,
) => {
  const offchainToken = userState.trading.offchainTokens[offchainTokenId];

  if (!offchainToken || !tx) {
    return false;
  }

  const hasSufficientTokens = Big(offchainToken.tokenAmount).gte(
    tx.tokenAmount,
  );

  const hasValidCoinAmount = getCoinAmountForTokenSell(
    tx.currentSupply,
    tx.tokenAmount,
  ).gte(1);

  return hasSufficientTokens && hasValidCoinAmount;
};

export const getTxWithinEstimate = (value: Big, estimate: Big, pct: number) => {
  const diff = value.minus(estimate).abs();
  const acceptableError = value.mul(pct - 1);
  return acceptableError.gte(diff);
};

export function getCanUserSell(
  state: State,
  cardId: string,
  tokenAmount: Big = Big(0),
) {
  const myTokenAmount = state.trading.offchainTokens[cardId]?.tokenAmount;
  if (!myTokenAmount) {
    return false;
  }
  return Big(myTokenAmount).gte(tokenAmount);
}

export async function confirmTransaction(
  state: State,
  api: ReplicantAsyncActionAPI<ReplicantServer>,
  offchainTokenId: string,
  timestamp: number,
): Promise<
  | {
      transaction: TradingTx;
      offchainToken: TradingState & { createdAt: number };
    }
  | undefined
> {
  await api.flushMessages();

  const offchainTokenState = await api.sharedStates.offchainTrading.fetch(
    offchainTokenId,
  );

  const offchainToken = offchainTokenState?.global;
  // This should be impossible
  if (!offchainToken) {
    throw new Error(
      `Cannot find offchainToken ${offchainTokenId} for transaction confirmation at time ${timestamp}`,
    );
  }

  const failedTx = offchainToken.failedTxs.find((failedTx) => {
    return failedTx.createdAt === timestamp && failedTx.userId === state.id;
  });

  if (failedTx) {
    if (failedTx.reason) {
      api.sendAnalyticsEvents([
        {
          eventType: 'OffchainTxFailed',
          eventProperties: {
            offchainTokenId,
            reason: failedTx.reason,
            createdAt: failedTx.createdAt,
          },
        },
      ]);
    }
    return;
  }

  const transaction = offchainToken.txs.find((tx) => {
    return tx.createdAt === timestamp && tx.userId === state.id;
  });

  if (transaction) {
    return {
      transaction,
      offchainToken,
    };
  }
}

export function getCurrencyInvested(state: State) {
  const ownedTokens = Object.values(state.trading.offchainTokens);
  return ownedTokens.reduce((totalInvested, ownedToken) => {
    return totalInvested.add(ownedToken.currencyInvested);
  }, Big(0));
}

export function computePortfolioValue(
  state: State,
  tokenStatuses: TradingSearchResult[],
) {
  const ownedTokens = state.trading.offchainTokens;
  const portfolioValue = tokenStatuses.reduce((totalValue, tokenStatus) => {
    const ownedToken = ownedTokens[tokenStatus.id];
    if (!ownedToken) {
      return totalValue;
    }

    const coinValue = getGrossCoinAmountForTokenSell(
      Big(tokenStatus.supply),
      Big(ownedToken.tokenAmount),
    );

    return totalValue.add(coinValue);
  }, Big(0));

  return portfolioValue;
}

export async function getPortfolioValue(
  state: MutableState,
  api:
    | ReplicantAsyncActionAPI<ReplicantServer>
    | ReplicantEventHandlerAPI<ReplicantServer>,
) {
  const tokenIds = getMyOffchainTokenIds(state);
  const tokenStatuses = await api.asyncGetters.getOffchainTokensFromOpenSearch({
    offchainTokenIds: tokenIds,
  });

  return computePortfolioValue(state, tokenStatuses);
}

export function getTradingVolume(state: State) {
  const { currencySpent, currencyRecovered } = state.trading.offchain;
  return Big(currencyRecovered).add(currencySpent);
}

export function getProfitLoss(state: State) {
  const { currencySpent, currencyRecovered } = state.trading.offchain;
  return Big(currencyRecovered).minus(currencySpent);
}

export function getPriceBackThen(pricePoints: PriceSlice[], then: number) {
  if (pricePoints.length === 0) {
    return Big(0);
  }

  let priceIdx = pricePoints.findIndex((pricePoint) => {
    return pricePoint.time > then;
  });

  if (priceIdx > 0) {
    // we want the price just before the slice that comes after the "then"
    priceIdx -= 1;
  } else {
    priceIdx = pricePoints.length - 1;
  }

  return Big(pricePoints[priceIdx].price);
}

export function getPriceChange(priceNow: Big, priceThen: Big) {
  if (priceThen.eq(0)) {
    return 0;
  }
  return priceNow.div(priceThen).minus(1).toNumber();
}

export function getMeanHoldings(state: TradingState) {
  return Big(state.supply).div(state.holderCount);
}

export function getVariance(state: TradingState) {
  if (state.holderCount <= 0) {
    return Big(0);
  }

  const meanHoldings = getMeanHoldings(state);
  const sumOfHoldingsSquare = Big(state.sumOfHoldingsSquare);
  const variance = sumOfHoldingsSquare
    .div(state.holderCount)
    .minus(meanHoldings.pow(2));

  // Handle potential floating-point errors
  if (variance.lt(0)) {
    return Big(0);
  }

  return variance;
}

// "ceofficient of variation" is a measure of inequality
export function getCoefficientOfVariation(state: TradingState) {
  if (state.holderCount === 1) {
    // everything held by a single person
    return 1;
  }

  if (state.holderCount <= 0) {
    // no one holds the token = fairest distribution
    return 0;
  }

  const meanHoldings = getMeanHoldings(state);
  if (meanHoldings.lte(0)) {
    // note that it should not happen
    // in this case we default to 1 to avoid promoting "broken" tokens
    return 1;
  }

  const holdingsSdToMean = getVariance(state).sqrt();
  const c = holdingsSdToMean.div(meanHoldings).toNumber();
  // normalize, constant chosen arbitrarily to make variation coefficient feel good
  return c / (c + 10);
}

export function hasSeenTokenDisplayChange(state: State) {
  return state.trading.hasSeenTokenDisplayChange ?? false;
}

export function hasTradedBefore(state: State) {
  return Big(state.trading.offchain.currencySpent).gt(0);
}
